import React, { useEffect, useMemo, useState } from "react";

import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import ForwardToInboxIcon from "@mui/icons-material/ForwardToInbox";
import {
  Autocomplete,
  Avatar,
  Button,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from "@mui/material";
import { AdvancedSearchFileIcon } from "assets/material-custom-icons";
import { FileViewer, Menu } from "components/popups";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import ReactHtmlParser from "html-react-parser";
import { useInvestorsSelector, useTaskDealsSelect } from "store";
import { FundFileType, ICategorizedFile } from "store/types";

import { FileType, fundFilesTypes } from "../../../../constants";
import { useEmailsContext } from "../context/Email-context";
import { Suggestion } from "./Suggestion";

interface IFileValues {
  file_id: number;
  type: FundFileType | null;
  investor_id: number | null;
}
const TableCellStyled = styled(TableCell)(() => ({
  padding: "6px 10px"
}));

export const EmailPreview = () => {
  const { emailPreviewId, onMove, setSuggestionDeal, emailsData, handleDelete } = useEmailsContext();
  const [filesValues, setFilesValues] = useState<IFileValues[]>([]);
  const investors = useInvestorsSelector();
  const deals = useTaskDealsSelect();
  const email = useMemo(
    () => emailsData.find((email) => email.id === emailPreviewId) || null,
    [emailPreviewId, emailsData]
  );

  const [fileViewer, setFileViewer] = useState<{ id: number; url: string; name: string; type: FileType } | null>(null);

  useEffect(() => {
    if (!email) return;
    if (email?.attachments?.length) {
      setFilesValues(
        email?.attachments?.map((file) => {
          return { file_id: file.id, type: null, investor_id: null };
        })
      );
    } else {
      setFilesValues([]);
    }
  }, [email?.attachments]);

  const isFilesValuesValid = useMemo(() => {
    if (!filesValues.length) return true;
    return !filesValues.some((item) => item.investor_id && !item.type);
  }, [filesValues]);

  const associateTooltipMessage = useMemo(() => {
    if (!isFilesValuesValid) return "File type is required when the investor field is selected";
    if (!email?.suggestions?.[0]?.deal) return "Please select a deal";
    return "";
  }, [isFilesValuesValid, email?.suggestions?.[0]?.deal, filesValues]);

  const renderFileCategorizing = useMemo(() => {
    return email?.deal_id ? (
      <Stack
        direction="column"
        gap="15px"
        sx={{ maxHeight: "400px", overflowY: "auto", width: "100%", padding: "0px 40px" }}
      >
        {email?.attachments.map((file, index) => (
          <Typography
            onClick={() =>
              file.file_url && setFileViewer({ id: file.id, url: file.file_url, name: file.name, type: file.type })
            }
            style={{ cursor: "pointer", color: "#26a6ae" }}
            key={index}
          >
            {file.name}
          </Typography>
        ))}
      </Stack>
    ) : (
      <Stack sx={{ width: "100%", overflowY: "hidden", padding: "0px 40px" }}>
        <TableContainer sx={{ maxHeight: "400px" }}>
          <Table>
            <TableHead sx={{ bgcolor: "#F6F8FC" }}>
              <TableRow>
                <TableCellStyled>File name</TableCellStyled>
                <TableCellStyled>File type</TableCellStyled>
                <TableCellStyled>Investor</TableCellStyled>
              </TableRow>
            </TableHead>
            <TableBody>
              {email?.attachments.map((file, index) => (
                <TableRow key={index}>
                  <TableCellStyled
                    style={{ cursor: "pointer", color: "#26a6ae" }}
                    onClick={() =>
                      file.file_url &&
                      setFileViewer({ id: file.id, url: file.file_url, name: file.name, type: file.type })
                    }
                  >
                    {file.name}
                  </TableCellStyled>
                  <TableCellStyled>
                    <Autocomplete
                      sx={{ minWidth: 200 }}
                      options={fundFilesTypes}
                      value={filesValues.find((item) => item.file_id === file.id)?.type}
                      onChange={(_, option) => {
                        setFilesValues((prevState) =>
                          prevState.map((item) =>
                            item.file_id === file.id
                              ? {
                                  ...item,
                                  type: option
                                }
                              : item
                          )
                        );
                      }}
                      renderInput={(params) => <TextField {...params} />}
                    />
                  </TableCellStyled>
                  <TableCellStyled>
                    <Autocomplete
                      sx={{ minWidth: 200 }}
                      options={investors}
                      getOptionLabel={(option) => option.name}
                      value={investors.find(
                        (investor) => filesValues.find((item) => item.file_id === file.id)?.investor_id === investor.id
                      )}
                      onChange={(_, option) => {
                        setFilesValues((prevState) =>
                          prevState.map((item) =>
                            item.file_id === file.id
                              ? {
                                  ...item,
                                  investor_id: option?.id || null
                                }
                              : item
                          )
                        );
                      }}
                      renderInput={(params) => <TextField {...params} />}
                    />
                  </TableCellStyled>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>
    );
  }, [investors, email, deals, filesValues]);

  const emailSender = useMemo(() => {
    if (!email?.sender_name.length) return;
    if (email.sender_name.trim().indexOf(" ") !== -1) {
      //there is at least one space, excluding leading and training spaces
      return `${email.sender_name.split(" ")[0][0]}${email.sender_name.split(" ")[1][0]}`;
    } else return `${email.sender_name.split("")[0][0]}`;
  }, [email?.sender_name]);

  const isAssociated = Boolean(email?.deal_id);

  const formattedDate = email
    ? Intl.DateTimeFormat("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
        day: "numeric",
        month: "short"
      }).format(new Date(email.created_at))
    : "";

  return (
    <Stack
      gap="10px"
      sx={{
        padding: "10px",
        width: "100%",
        height: "100%",
        borderRadius: "10px",
        backgroundColor: "white",
        boxShadow: "0 -1px 5px 1px rgba(0,0,0,.08)"
      }}
    >
      {!email ? (
        <Stack
          sx={{
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center"
          }}
        >
          <Typography color="secondary">No email selected</Typography>
        </Stack>
      ) : (
        <>
          <Stack direction="row">
            <Stack sx={{ display: "flex", flexDirection: "row", width: "100%", gap: "6px" }}>
              <Avatar
                sx={{ backgroundColor: "#3a5b9f", width: 30, height: 30, fontSize: "10px" }}
                alt={email.sender_name}
                src={"/static/images/avatar/11.jpg"}
              >
                {emailSender}
              </Avatar>
              <Stack>
                <MaterialTooltip title={email.sender_name} isShow>
                  <Typography
                    sx={{ margin: 0, maxWidth: "255px" }}
                    variant="body2"
                    color="primary"
                    fontWeight={700}
                    lineHeight={1}
                    whiteSpace="nowrap"
                    overflow="hidden"
                    textOverflow="ellipsis"
                  >
                    {email.sender_name}
                  </Typography>
                </MaterialTooltip>
                <MaterialTooltip title={email.email_subject} isShow>
                  <Typography
                    sx={{ margin: 0, maxWidth: "600px" }}
                    variant="body2"
                    color="secondary"
                    fontSize="13px"
                    whiteSpace="nowrap"
                    overflow="hidden"
                    textOverflow="ellipsis"
                  >
                    {email.email_subject}
                  </Typography>
                </MaterialTooltip>
              </Stack>
            </Stack>
            <Stack alignItems="flex-end">
              <Typography sx={{ whiteSpace: "nowrap", fontSize: "14px", color: "#496682" }}>{formattedDate}</Typography>
              {Boolean(email.attachments.length) && (
                <AttachFileIcon sx={{ transform: "rotate(45deg)", fontSize: 20 }} />
              )}
            </Stack>
          </Stack>
          <Stack
            alignItems="center"
            justifyContent="space-between"
            direction="row"
            sx={{ backgroundColor: "#F6F8FC", paddingLeft: "40px", minHeight: "44px" }}
          >
            <Stack flexDirection="column">{<Suggestion email={email} setSuggestionDeal={setSuggestionDeal} />}</Stack>
            <Stack direction="row" gap="12px" alignItems="center">
              {!isAssociated && (
                <MaterialTooltip title={associateTooltipMessage} isShow>
                  <span>
                    <Button
                      disabled={!email?.suggestions?.[0]?.deal || !isFilesValuesValid}
                      size="sm"
                      sx={{ padding: "0 12px", minWidth: 0 }}
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        email?.suggestions?.[0]?.deal &&
                          onMove({
                            email,
                            deal: email.suggestions?.[0]?.deal,
                            categorizedFiles: filesValues.length ? (filesValues as ICategorizedFile[]) : undefined
                          });
                      }}
                      startIcon={<ForwardToInboxIcon />}
                    >
                      Associate
                    </Button>
                  </span>
                </MaterialTooltip>
              )}

              <Menu
                options={[
                  {
                    title: "Delete",
                    icon: <DeleteOutlineOutlinedIcon />,
                    onClick: () => handleDelete(email.id)
                  }
                ]}
              />
            </Stack>
          </Stack>
          <Stack gap="10px" sx={{ padding: "0px 40px", minWidth: 0, maxWidth: "100%" }}>
            <Typography
              color="#0A3142"
              lineHeight="1"
              overflow="auto"
              maxHeight={email?.attachments?.length ? "45vh" : "70vh"}
              sx={{
                minWidth: 0,
                maxWidth: "100%",
                wordBreak: "break-word",
                overflowWrap: "break-word",
                "::-webkit-scrollbar-thumb": {
                  backgroundColor: "#DAE0E2"
                },
                "& html": {
                  overflow: "auto !important",
                  height: "50vh !important",
                  maxHeight: "50vh !important",
                  minHeight: "50px !important"
                }
              }}
            >
              {ReactHtmlParser(email.email_body)}
            </Typography>
          </Stack>
          {Boolean(email.attachments.length) && (
            <>
              <Stack
                direction="row"
                alignItems="center"
                gap="10px"
                sx={{ margin: "0px 40px", borderBottom: "1px solid #DBE8F5", marginBottom: "5px" }}
              >
                <AdvancedSearchFileIcon sx={{ width: "16px", height: "16px" }} />
                <Typography color="#496682" fontWeight={400} fontSize="14px">
                  {`Files (${email?.attachments?.length})`}
                </Typography>
              </Stack>
              {renderFileCategorizing}
            </>
          )}
        </>
      )}
      {fileViewer && (
        <FileViewer
          file={{ file_url: fileViewer.url, type: fileViewer.type }}
          isOpen={Boolean(fileViewer)}
          onClose={() => setFileViewer(null)}
        />
      )}
    </Stack>
  );
};
